import { useQuery } from "@tanstack/react-query";
import { featureFlagsApi, type FeatureFlagDto } from "@/api/endpoints";

/**
 * Map of every flag key the tenant is opted into. Returned as a Set so call-sites that
 * just want a yes/no can do `flags.has("hospital.ward")` without a lookup.
 *
 * Cached for 5 minutes so opening a few sidebar groups in quick succession does not
 * thrash the API. After a toggle in the admin page we invalidate the query so the
 * sidebar refreshes inside a tick.
 */
export const FEATURE_FLAGS_QUERY_KEY = ["feature-flags"] as const;

export interface FeatureFlagsState {
  /** True if the flag is enabled for the caller's tenant. */
  isEnabled: (featureKey: string) => boolean;
  /** Raw list — used by the admin page. */
  rows: FeatureFlagDto[];
  /** True until the first /feature-flags response lands. */
  isLoading: boolean;
}

export function useFeatureFlags(): FeatureFlagsState {
  const query = useQuery({
    queryKey: FEATURE_FLAGS_QUERY_KEY,
    queryFn: featureFlagsApi.list,
    staleTime: 5 * 60_000,
    // Sidebar is rendered before login on the public routes, but useFeatureFlags is only
    // invoked from inside <AuthGuard /> so we don't need to gate by user here.
  });

  const enabled = new Set(
    (query.data ?? []).filter(r => r.isEnabled).map(r => r.featureKey),
  );

  return {
    isEnabled: (key) => enabled.has(key),
    rows: query.data ?? [],
    isLoading: query.isLoading,
  };
}
